import { useState, useEffect, useRef } from "react";
import { uploadFileToVps } from "@/lib/vpsStorage";

/* ─── Types ──────────────────────────────────────────────────── */
export type GalleryRow = {
  id: string;
  tenant_id: string;
  image_url: string;
  title: string | null;
  category: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
};

export type GalleryForm = {
  image_url: string;
  title: string;
  category: string;
  is_active: boolean;
};

const EMPTY: GalleryForm = {
  image_url: "",
  title: "",
  category: "",
  is_active: true,
};

const INPUT_CLASS =
  "w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 focus:ring-2 focus:ring-[#C8A96E]/10 transition-all";

const CATEGORY_SUGGESTIONS = [
  "Weddings", "XV Años", "Engagements", "Baptisms", "Graduations",
  "Birthday Parties", "Corporate Events", "Family Sessions", "Baby Showers", "Other",
];

interface Props {
  open:    boolean;
  editing: GalleryRow | null;
  saving:  boolean;
  tenantId: string;
  onClose: () => void;
  onSave:  (form: GalleryForm) => void;
}

export default function GalleryModal({ open, editing, saving, tenantId, onClose, onSave }: Props) {
  const [form, setForm]         = useState<GalleryForm>(EMPTY);
  const [urlError, setUrlError] = useState("");
  const [imgOk, setImgOk]       = useState(false);
  const [imgError, setImgError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setForm({
        image_url: editing.image_url,
        title: editing.title ?? "",
        category: editing.category ?? "",
        is_active: editing.is_active,
      });
      setImgOk(true);
      setImgError(false);
      setUrlError("");
      setUploadError(null);
    } else {
      setForm(EMPTY);
      setImgOk(false);
      setImgError(false);
      setUrlError("");
      setUploadError(null);
    }
  }, [editing, open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  const set = <K extends keyof GalleryForm>(key: K, val: GalleryForm[K]) =>
    setForm((p) => ({ ...p, [key]: val }));

  const handleUrlChange = (url: string) => {
    set("image_url", url);
    setUrlError("");
    setImgOk(false);
    setImgError(false);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    try {
      const result = await uploadFileToVps({
        file,
        tenantId,
        folder: "website",
        allowedTypes: ["image/*"],
        maxSizeMB: 10,
      });
      if (!result.success || !result.url) {
        setUploadError(result.error || "Upload failed");
      } else {
        handleUrlChange(result.url);
        setImgOk(true);
        setImgError(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const validate = (): boolean => {
    if (!form.image_url.trim()) {
      setUrlError("Image URL or uploaded file is required.");
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
  };

  if (!open) return null;

  const hasUrl = form.image_url.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl border border-slate-200 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
              <i className={`${editing ? "ri-image-edit-line" : "ri-image-add-line"} text-[#C8A96E]`}></i>
            </div>
            <h2 className="text-slate-800 font-semibold text-sm">
              {editing ? "Edit Photo" : "Add Photo"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[68vh] overflow-y-auto">

          {/* File upload */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Upload Image
            </label>
            <div className={`relative rounded-xl border-2 border-dashed transition-all ${
              uploading ? "border-[#C8A96E]/40 bg-[#C8A96E]/5" : "border-slate-200 hover:border-[#C8A96E]/40 cursor-pointer"
            }`}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                disabled={uploading}
                className="hidden"
                onChange={handleFileChange}
              />
              <div className="flex flex-col items-center gap-2 py-4 px-4 text-center">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                  uploading ? "bg-[#C8A96E]/15 text-[#C8A96E]" : "bg-slate-100 text-slate-400"
                }`}>
                  {uploading ? (
                    <i className="ri-loader-4-line animate-spin text-sm"></i>
                  ) : (
                    <i className="ri-upload-line text-sm"></i>
                  )}
                </div>
                <p className="text-xs font-medium text-[#0f172a]">
                  {uploading ? "Uploading…" : "Click to upload image"}
                </p>
                {!uploading && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#2563eb] text-white hover:bg-blue-700 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-upload-line text-[10px]"></i>
                    Choose File
                  </button>
                )}
                {uploadError && (
                  <p className="text-red-500 text-[10px]">{uploadError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-slate-400 text-[11px] font-medium whitespace-nowrap">or paste URL</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Image URL */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Image URL <span className="text-red-400">*</span>
            </label>
            <input
              type="url"
              value={form.image_url}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://your-domain.com/photo.jpg"
              className={`${INPUT_CLASS} ${urlError ? "border-red-300 focus:border-red-300" : ""}`}
              autoFocus={!editing}
            />
            {urlError && <p className="text-red-400 text-xs mt-1">{urlError}</p>}
            {!urlError && (
              <p className="text-slate-400 text-[10px] mt-1">
                Paste a direct image link (JPG, PNG, WebP, etc.) or upload above.
              </p>
            )}
          </div>

          {/* Image preview */}
          {hasUrl && (
            <div className="relative rounded-xl overflow-hidden bg-slate-100 border border-slate-200 h-44">
              <img
                src={form.image_url}
                alt="Preview"
                className="w-full h-full object-cover object-top"
                onLoad={() => { setImgOk(true); setImgError(false); }}
                onError={() => { setImgOk(false); setImgError(true); }}
              />
              {!imgOk && !imgError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-slate-400 bg-slate-100">
                  <i className="ri-image-line text-2xl"></i>
                  <p className="text-xs">Loading preview…</p>
                </div>
              )}
              {imgError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-red-50">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                  <p className="text-red-500 text-xs">Couldn&apos;t load image — check the URL</p>
                </div>
              )}
              {imgOk && (
                <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg bg-black/40 border border-white/10">
                  <i className="ri-check-line text-emerald-400 text-[10px]"></i>
                  <span className="text-white/80 text-[10px]">Image loaded</span>
                </div>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Title
              <span className="text-slate-300 text-[10px] normal-case tracking-normal ml-1.5">optional</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              maxLength={80}
              placeholder="e.g. Summer Wedding at Lake Como"
              className={INPUT_CLASS}
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Category
              <span className="text-slate-300 text-[10px] normal-case tracking-normal ml-1.5">optional</span>
            </label>
            <input
              type="text"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              maxLength={40}
              placeholder="e.g. Weddings"
              className={`${INPUT_CLASS} mb-2`}
            />
            <div className="flex flex-wrap gap-1.5">
              {CATEGORY_SUGGESTIONS.map((cat) => {
                const isSelected = form.category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => set("category", isSelected ? "" : cat)}
                    className={`px-2.5 py-1 rounded-full text-[10px] border transition-all cursor-pointer whitespace-nowrap ${
                      isSelected
                        ? "bg-[#C8A96E]/15 border-[#C8A96E]/40 text-[#9a7840] font-medium"
                        : "bg-white border-slate-200 text-slate-500 hover:border-[#C8A96E]/30 hover:text-slate-700"
                    }`}
                  >
                    {isSelected && <i className="ri-check-line mr-0.5 text-[9px]"></i>}
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-slate-50 border border-slate-200">
            <div>
              <p className="text-slate-700 text-sm font-medium">Show on public page</p>
              <p className="text-slate-400 text-xs mt-0.5">
                {form.is_active ? "Visible in portfolio gallery" : "Hidden from visitors"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => set("is_active", !form.is_active)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                form.is_active ? "bg-emerald-500" : "bg-slate-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  form.is_active ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {saving || uploading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <i className="ri-save-line"></i>
            )}
            {saving || uploading ? "Saving…" : editing ? "Save Changes" : "Add Photo"}
          </button>
        </div>
      </div>
    </div>
  );
}